import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ParkingSquare, LayoutGrid, Building2, LogOut, ShieldCheck, UserCog, Car, CreditCard, Tags, Sparkles, Package, Wallet, ScrollText, Ticket, Coins, ClipboardCheck, Settings, Moon, LayoutDashboard, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import parknovaLogo from "@/assets/brand/parknova-logo.png";

const ROLE_LABELS = {
  SuperAdmin: "Super Administrador",
  TenantAdmin: "Administrador",
  Cajero: "Cajero",
  Valet: "Valet",
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendientes, setPendientes] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user?.rol !== "SuperAdmin") return;
    let alive = true;
    const fetchCount = () => api.get("/admin/recargas/pendientes-count")
      .then(({ data }) => { if (alive) setPendientes(data.pendientes || 0); })
      .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 30000); // refresco periódico
    window.addEventListener("recargas:refresh", fetchCount);
    return () => { alive = false; clearInterval(t); window.removeEventListener("recargas:refresh", fetchCount); };
  }, [user?.rol, location.pathname]);

  // Cierra el panel móvil al cambiar de ruta (por si se navega desde otro punto).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Bloquea el scroll del fondo mientras el panel móvil está abierto.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const nav = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/app/dashboard", roles: ["TenantAdmin"] },
    { label: "Sucursales", icon: LayoutGrid, path: "/app", roles: ["TenantAdmin"] },
    { label: "Empresas", icon: Building2, path: "/app/empresas", roles: ["SuperAdmin"] },
    { label: "Usuarios", icon: UserCog, path: "/app/usuarios", roles: ["SuperAdmin", "TenantAdmin"] },
    { label: "Bitácora", icon: ScrollText, path: "/app/bitacora", roles: ["SuperAdmin", "TenantAdmin"] },
    { label: "Paquetes de Folios", icon: Ticket, path: "/app/paquetes", roles: ["SuperAdmin"] },
    { label: "Validación de Recargas", icon: ClipboardCheck, path: "/app/validacion-recargas", roles: ["SuperAdmin"], badge: true },
    { label: "Configuración del Sistema", icon: Settings, path: "/app/configuracion-sistema", roles: ["SuperAdmin"] },
    { label: "Recarga de Folios", icon: Coins, path: "/app/recargas", roles: ["TenantAdmin"] },
    { label: "Pensionados", icon: Wallet, path: "/app/pensiones", roles: ["TenantAdmin"] },
    { label: "Tarifas y precios", icon: Tags, path: "/app/tarifas", roles: ["TenantAdmin"] },
    { label: "Servicios adicionales", icon: Sparkles, path: "/app/servicios", roles: ["TenantAdmin"] },
    { label: "Productos", icon: Package, path: "/app/productos", roles: ["TenantAdmin"] },
    { label: "Integraciones financieras", icon: CreditCard, path: "/app/configuracion-financiera", roles: ["TenantAdmin"] },
    { label: "Cortes de Caja", icon: Coins, path: "/app/cortes", roles: ["TenantAdmin"] },
    { label: "Pensiones Nocturnas", icon: Moon, path: "/app/pernoctas", roles: ["TenantAdmin"] },
    { label: "Auditoría Factor de Riesgo", icon: ShieldCheck, path: "/app/auditoria-factor", roles: ["TenantAdmin"] },
  ].filter((n) => n.roles.includes(user?.rol));

  // Render compartido de los items de navegación (misma lista y permisos en escritorio y móvil).
  const renderNav = ({ idPrefix, badgeTestId, onAfter }) =>
    nav.map((item) => {
      const active = location.pathname === item.path;
      const Icon = item.icon;
      return (
        <button
          key={item.path}
          onClick={() => { navigate(item.path); onAfter?.(); }}
          data-testid={`${idPrefix}${item.label.toLowerCase()}`}
          className={`w-full flex items-center gap-3 px-4 h-11 text-sm font-medium transition-colors duration-200 ${
            active ? "bg-[#FFCC00] text-[#0A0A0A] font-bold" : "text-white/70 hover:bg-white/10 hover:text-white"
          }`}
        >
          <Icon className="h-4 w-4" strokeWidth={2.2} />
          <span className="flex-1 text-left">{item.label}</span>
          {item.badge && pendientes > 0 && (
            <span data-testid={badgeTestId} className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-black rounded-full bg-[#EF4444] text-white">
              {pendientes}
            </span>
          )}
        </button>
      );
    });

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Sidebar (escritorio) — sin cambios */}
      <aside className="hidden md:flex flex-col w-64 bg-[#0A0A0A] text-white shrink-0">
        <div className="flex items-center px-5 h-20 border-b border-white/10">
          <div className="bg-white rounded-xl px-3.5 py-2.5 shadow-sm">
            <img src={parknovaLogo} alt="ParkNova" className="h-8 w-auto object-contain block select-none" draggable="false" />
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {renderNav({ idPrefix: "nav-", badgeTestId: "recargas-pendientes-badge" })}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 h-11 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Panel de navegación móvil (drawer) — solo en celular/tablet (<md) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" data-testid="mobile-drawer">
          <div className="relative flex flex-col w-72 max-w-[82%] h-full bg-[#0A0A0A] text-white shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-5 h-20 border-b border-white/10">
              <div className="bg-white rounded-xl px-3.5 py-2.5 shadow-sm">
                <img src={parknovaLogo} alt="ParkNova" className="h-8 w-auto object-contain block select-none" draggable="false" />
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-drawer-close"
                aria-label="Cerrar menú"
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="h-6 w-6" strokeWidth={2.2} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
              {renderNav({ idPrefix: "mobile-nav-", badgeTestId: "mobile-recargas-pendientes-badge", onAfter: () => setMobileOpen(false) })}
            </nav>
            <div className="px-3 py-4 border-t border-white/10">
              <button
                onClick={() => { setMobileOpen(false); logout(); navigate("/"); }}
                data-testid="mobile-logout-btn"
                className="w-full flex items-center gap-3 px-4 h-11 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-200"
              >
                <LogOut className="h-4 w-4" /> Cerrar sesión
              </button>
            </div>
          </div>
          {/* Backdrop: cierra al tocar fuera del panel */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
            aria-label="Cerrar menú"
            className="flex-1 bg-black/60"
          />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-20 bg-white border-b border-[#e4e4e7] flex items-center justify-between px-6 lg:px-8 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              data-testid="admin-hamburger-btn"
              aria-label="Abrir menú"
              className="md:hidden -ml-1 p-2 rounded-lg text-[#0A0A0A] hover:bg-[#0A0A0A]/5 transition-colors"
            >
              <Menu className="h-6 w-6" strokeWidth={2.2} />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">
              <ShieldCheck className="h-4 w-4 text-[#FFCC00]" />
              Consola de administración
            </div>
          </div>
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-right min-w-0">
              <p className="text-sm font-bold leading-none truncate max-w-[45vw] sm:max-w-none" data-testid="header-user">{user?.email || user?.usuario_corto}</p>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] mt-1">{ROLE_LABELS[user?.rol]}</p>
            </div>
            <div className="h-10 w-10 bg-[#0A0A0A] text-[#FFCC00] font-display font-black flex items-center justify-center shrink-0">
              {(user?.email || user?.usuario_corto || "?")[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
